import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CustomFieldConditionEnum } from 'src/app/shared/components/conditions-on-custom-fields/custom-field-condition.enum';
import { API_URL_PREFIX, BasicService, JSON_CONTENT_TYPE_HEADER } from '../shared/services/basic.service';
import { ErrorHandlingService } from '../shared/services/error-handling.service';
import { CustomFieldsContext } from '../task-process/shared/model';
import {
  CustomField,
  CustomFieldDefinition,
  CustomFieldPhoneNumber,
  CustomFieldTypeName,
  CustomFieldValue,
  CustomFieldsForTaskProcessPage,
  FieldValuesReponse,
} from './custom-fields.model';

@Injectable({
  providedIn: 'root',
})
export class CustomFieldsService {
  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService,
    private basicService: BasicService
  ) {}

  updateUserFieldValues(userId: number, valuesByFieldId): Observable<FieldValuesReponse> {
    const url = `${API_URL_PREFIX}/api/1.0/custom-field/fwd/users/${userId}`;
    const body = this.formatFieldValuesForBackend(valuesByFieldId);
    return this.http
      .put<FieldValuesReponse>(url, body, JSON_CONTENT_TYPE_HEADER)
      .pipe(catchError((response: HttpErrorResponse) => this.handleUpdateFieldError(response)));
  }

  updateConversationFieldValues(conversationId: number, valuesByFieldId): Observable<FieldValuesReponse> {
    const url = `${API_URL_PREFIX}/api/1.0/custom-field/fwd/conversations/${conversationId}`;
    const body = this.formatFieldValuesForBackend(valuesByFieldId);
    return this.http
      .put<FieldValuesReponse>(url, body, JSON_CONTENT_TYPE_HEADER)
      .pipe(catchError((response: HttpErrorResponse) => this.handleUpdateFieldError(response)));
  }

  private handleUpdateFieldError(response: HttpErrorResponse) {
    if (!this.hasValidationErrors(response)) {
      this.errorHandlingService.toastAlertUnknownError(response);
    }
    return throwError(response);
  }

  hasValidationErrors(response: HttpErrorResponse) {
    // TODO : validation errors shall be in response.error.details.validation_errors
    return response.error?.details && Array.isArray(response.error.details);
  }

  private formatFieldValuesForBackend(valuesByFieldId: { [id: string]: CustomFieldValue }) {
    return Object.keys(valuesByFieldId).map(id => ({
      field_id: Number(id),
      field_value: valuesByFieldId[id],
    }));
  }

  getFieldsForTaskProcessPage(customFieldContext: CustomFieldsContext): Observable<CustomFieldsForTaskProcessPage> {
    const url = `${API_URL_PREFIX}/api/1.0/custom-field/for-task-processing-page`;
    const options = {
      params: new HttpParams()
        .set('custom_field_user_id', String(customFieldContext.customFieldUserId))
        .set('custom_field_conversation_id', String(customFieldContext.customFieldConversationId)),
    };
    return this.http.get<any>(url, options).pipe(
      map(response => this.adaptFieldsForTaskProcessPage(response)),
      this.errorHandlingService.catchAlertAndThrowHttpError()
    );
  }

  private adaptFieldsForTaskProcessPage(backendResponse) {
    return {
      user: backendResponse.user.map(field => this.adaptCustomFieldFromBackend(field)),
      conversation: backendResponse.conversation.map(field => this.adaptCustomFieldFromBackend(field)),
    };
  }

  private adaptCustomFieldFromBackend(backendField): CustomField {
    const result = {
      value: backendField.field_value,
      definition: backendField.field_definition,
      isEditable: backendField.is_editable,
    };
    // temp workaround for backend returning 0/1
    if (result.definition.type.type === CustomFieldTypeName.Boolean) {
      result.value = !!result.value;
    }
    return result;
  }

  getFields(): Observable<CustomFieldDefinition[]> {
    return this.http.get<any[]>(`${API_URL_PREFIX}/api/1.0/custom-field/fwd/fields`).pipe(
      catchError(() => of([])),
      map(response => response.map(this.formatCustomFieldsForScenarioEditor))
    );
  }

  private formatCustomFieldsForScenarioEditor = (fieldData: any): CustomFieldDefinition => {
    return {
      id: fieldData.id,
      type: fieldData.type,
      label: fieldData.label,
      description: fieldData.description,
      scope: fieldData.scope,
    };
  };

  private buildPhoneFieldValueForBackend(fieldValue: CustomFieldPhoneNumber): any {
    return {
      country_prefix: fieldValue.countryCode,
      number: fieldValue.phoneNumber,
    };
  }

  convertFieldValueForBackend(fieldValue, field: CustomField) {
    let result = fieldValue;
    if (fieldValue) {
      switch (field.definition.type.type) {
        case CustomFieldTypeName.Phone:
          result = this.buildPhoneFieldValueForBackend(fieldValue);
          break;
        case CustomFieldTypeName.Date:
          result = fieldValue.substring(0, 10); // 2021-04-25
          break;
        case CustomFieldTypeName.Datetime:
          result = fieldValue.substring(0, 19); // 2020-08-27T13:44:02
          break;
        default:
          break;
      }
    }
    return result;
  }

  buildPhoneFieldValueForFormControl(fieldValue: any): CustomFieldPhoneNumber {
    return {
      countryCode: fieldValue.country_prefix,
      phoneNumber: fieldValue.number,
    };
  }

  updateDescription(elementData, customFieldsMap: Map<number, CustomFieldDefinition>) {
    if (elementData) {
      const { selectedCondition, selectedCustomField, comparedValue } = elementData || {};
      if (selectedCondition && selectedCustomField && customFieldsMap.has(selectedCustomField)) {
        const { label, type } = customFieldsMap.get(selectedCustomField);
        const decodedComparedValue = this.formatComparedValuebyType(type, comparedValue);
        const selectedCustomFieldLabel = label;
        let description;
        switch (selectedCondition) {
          case CustomFieldConditionEnum.IS_NULL:
            description = this.basicService.translate(
              'parameters/quick_replies/details/conditions_on_custom_fields/description/is_null',
              { customField: selectedCustomFieldLabel }
            );
            break;
          case CustomFieldConditionEnum.IS_NOT_NULL:
            description = this.basicService.translate(
              'parameters/quick_replies/details/conditions_on_custom_fields/description/is_not_null',
              { customField: selectedCustomFieldLabel }
            );
            break;
          case CustomFieldConditionEnum.CONTAINS:
            description = this.basicService.translate(
              'parameters/quick_replies/details/conditions_on_custom_fields/description/contains',
              {
                customField: selectedCustomFieldLabel,
                comparedValue: decodedComparedValue,
              }
            );
            break;
          case CustomFieldConditionEnum.DOES_NOT_CONTAIN:
            description = this.basicService.translate(
              'parameters/quick_replies/details/conditions_on_custom_fields/description/does_not_contain',
              {
                customField: selectedCustomFieldLabel,
                comparedValue: decodedComparedValue,
              }
            );
            break;
          case CustomFieldConditionEnum.BIGGER_THAN:
            description = this.basicService.translate(
              'parameters/quick_replies/details/conditions_on_custom_fields/description/bigger_than',
              {
                customField: selectedCustomFieldLabel,
                comparedValue: decodedComparedValue,
              }
            );
            break;
          case CustomFieldConditionEnum.LOWER_THAN:
            description = this.basicService.translate(
              'parameters/quick_replies/details/conditions_on_custom_fields/description/lower_than',
              {
                customField: selectedCustomFieldLabel,
                comparedValue: decodedComparedValue,
              }
            );
            break;
          case CustomFieldConditionEnum.EQUAL:
            description = this.basicService.translate(
              'parameters/quick_replies/details/conditions_on_custom_fields/description/equal',
              {
                customField: selectedCustomFieldLabel,
                comparedValue: decodedComparedValue,
              }
            );
            break;
          case CustomFieldConditionEnum.NOT_EQUAL:
            description = this.basicService.translate(
              'parameters/quick_replies/details/conditions_on_custom_fields/description/not_equal',
              {
                customField: selectedCustomFieldLabel,
                comparedValue: decodedComparedValue,
              }
            );
            break;
          default:
            description = this.basicService.translate('parameters/quick_replies/details/add_a_custom_field');
            break;
        }
        return description;
      }
    }
  }

  private formatComparedValuebyType(customFieldType, comparedValue): string {
    let decodedComparedValue = comparedValue;
    if (['enum', 'multipicklist'].includes(customFieldType.type)) {
      decodedComparedValue = customFieldType.allowed.find(({ id }) => comparedValue === id)?.label ?? comparedValue;
    } else if (customFieldType.type === 'boolean') {
      decodedComparedValue = this.basicService.translate(comparedValue ? 'yes' : 'no');
    } else if (customFieldType.type === 'date') {
      decodedComparedValue = moment(comparedValue, 'YYYY-MM-DD').format('DD/MM/YYYY');
    } else if (customFieldType.type === 'datetime') {
      decodedComparedValue = moment(comparedValue, 'YYYY-MM-DDTHH:mm:ss').format('DD/MM/YYYY HH:mm');
    }
    return decodedComparedValue;
  }
}
